import * as React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Tooltip from '@mui/material/Tooltip';
import InfoIcon from '@mui/icons-material/Info';
import { Translate } from '../../shared/LanguageService';
import { useFeatureFlag } from '../../shared/featureFlags/useFeatureFlag';
import { FeatureFlagKeys } from '../../shared/featureFlags/featureFlagKeys';
import { AuthenticatedUser, UserRole } from '../../shared/types';
import SuccessSnackbar from '../../shared/feedback/SuccessSnackbar';
import FailureSnackbar from '../../shared/feedback/FailureSnackbar';
import { DroneBookingsWarningDialog } from './DroneBookingsWarningDialog';
import IUpcomingDroneBooking from '../photographers/IUpcomingDroneBooking';
import { useDroneAvailabilityQuery } from './queries/useDroneAvailabilityQuery';
import { useUpdateDroneAvailabilityMutation } from './queries/useUpdateDroneAvailabilityMutation';

type DroneAvailabilitySectionProps = {
    user: AuthenticatedUser;
};

export function DroneAvailabilitySection({ user }: DroneAvailabilitySectionProps) {
    let isPhotographer = Boolean(
        user && user.details && user.details.role === UserRole.Photographer
    );
    let flagEnabled = useFeatureFlag(FeatureFlagKeys.PhotographerDroneToggle, false);

    let { data } = useDroneAvailabilityQuery(isPhotographer && flagEnabled);
    let updateDroneAvailability = useUpdateDroneAvailabilityMutation();

    let [confirmingUncheck, setConfirmingUncheck] = React.useState(false);
    let [showSaved, setShowSaved] = React.useState(false);
    let [showFailed, setShowFailed] = React.useState(false);

    if (!data || !data.canEdit) {
        return null;
    }

    let upcomingBookings: IUpcomingDroneBooking[] = data.upcomingDroneBookings
        ? data.upcomingDroneBookings
        : [];

    function save(hasDrone: boolean) {
        setConfirmingUncheck(false);
        updateDroneAvailability.mutate(hasDrone, {
            onSuccess: () => setShowSaved(true),
            onError: () => setShowFailed(true)
        });
    }

    function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
        let checked = event.target.checked;

        if (!checked && upcomingBookings.length > 0) {
            setConfirmingUncheck(true);
            return;
        }

        save(checked);
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h2" sx={{ fontSize: '24px', padding: '16px 0' }}>
                {Translate('profile/drone/title')}
                <Tooltip title={Translate('profile/drone/description')} placement="top-start">
                    <InfoIcon />
                </Tooltip>
            </Typography>
            <FormControlLabel
                control={
                    <Checkbox
                        name="hasDrone"
                        checked={data.hasDrone}
                        disabled={updateDroneAvailability.isLoading}
                        onChange={handleChange}
                    />
                }
                label={Translate('profile/drone/checkbox-label')}
            />
            <DroneBookingsWarningDialog
                open={confirmingUncheck}
                bookings={upcomingBookings}
                onConfirm={() => save(false)}
                onClose={() => setConfirmingUncheck(false)}
            />
            <SuccessSnackbar
                open={showSaved}
                onClose={() => setShowSaved(false)}
                text={Translate('profile/drone/saved')}
            />
            <FailureSnackbar
                open={showFailed}
                onClose={() => setShowFailed(false)}
                text={Translate('profile/drone/save-failed')}
            />
        </Box>
    );
}

export default DroneAvailabilitySection;
